import { formatRate, formatPositiveAmount } from 'src/utils/formatAmout';
import selectFormatDate from 'src/utils/selectDateFormat';
import { translateGracePeriodValueToDaysForFirstRent } from 'src/utils/quotes';

const ContractStatus = {
  DRAFT: 'DRAFT',
  PENDING_SIGNATURE: 'PENDING_SIGNATURE',
  IN_PROGRESS: 'IN_PROGRESS',
  ENDED: 'ENDED',
  CANCELLED: 'CANCELLED',
  SIGNED: 'SIGNED',
  CEDED: 'CEDED'
};

export const ContractPaymentStatus = {
  UP_TO_DATE: 'UP_TO_DATE',
  OVERDUE: 'OVERDUE'
};

export const ContributionStatus = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED'
};

export const AmendingAgreementSignStatus = {
  SIGNED: 'SIGNED',
  PENDING_SIGNATURE: 'PENDING_SIGNATURE'
};

export const ContractCessionSignStatus = {
  DRAFT: 'DRAFT',
  SIGNED: 'SIGNED',
  PENDING_SIGNATURE: 'PENDING_SIGNATURE'
};

export const ContractEarlyTerminationSignStatus = {
  SIGNED: 'SIGNED',
  PENDING_SIGNATURE: 'PENDING_SIGNATURE'
};

export const AutomaticDebitRequestSignStatus = {
  DRAFT: 'DRAFT',
  PENDING_TO_SIGN: 'PENDING_TO_SIGN',
  SIGNED: 'SIGNED'
};

export const hasContractDigitalSign = contract => {
  return Boolean(contract.weetrustDocId);
};

export const hasPromissoryNoteDigitalSign = promissoryNote => {
  return Boolean(promissoryNote.weetrustDocId);
};

export const ContractDetailTabsIndex = {
  DETAIL: 'detail',
  AVALS: 'avals',
  CONTRIBUTIONS: 'contributions',
  FISCAL_BALANCE: 'fiscal-balance',
  PAYMENT_SCHEDULE: 'payment-schedule',
  ACCOUNTING_STATE: 'accounting-state',
  CONTRACT_EVENTS: 'contract-events',
  ACCOUNTING_BALANCE: 'accounting-balance'
};

export const FiscalBalanceSubTabsIndex = {
  CAPITAL: 'capital',
  INTEREST: 'interest'
};

export const ContractCreationOption = {
  CREATE_CONTRACT: 'CREATE_CONTRACT',
  CREATE_CONTRACT_TO_DIGITAL_SIGN: 'CREATE_CONTRACT_TO_DIGITAL_SIGN'
};

export const ContractActionsOptions = {
  SEND_TO_DIGITAL_SIGN: 'SEND_TO_DIGITAL_SIGN',
  SIGN_MANUALLY: 'SIGN_MANUALLY',
  RESEND_TO_SIGN: 'RESEND_TO_SIGN',
  ACTIVATE_AUTOMATIC_BILLING: 'ACTIVATE_AUTOMATIC_BILLING',
  DESACTIVATE_AUTOMATIC_BILLING: 'DESACTIVATE_AUTOMATIC_BILLING',
  EDIT_CONTRACT_DATES: 'EDIT_CONTRACT_DATES',
  GENERATE_AUTOMATIC_REQUEST_DOCUMENT: 'GENERATE_AUTOMATIC_REQUEST_DOCUMENT',
  REGENERATE_AUTOMATIC_REQUEST_DOCUMENT:
    'REGENERATE_AUTOMATIC_REQUEST_DOCUMENT',
  CANCEL_CONTRACT: 'CANCEL_CONTRACT',
  CEDE_CONTRACT: 'CEDE_CONTRACT',
  TERMINATE_CONTRACT_EARLY: 'TERMINATE_CONTRACT_EARLY',
  CANCEL_CONTRACT_CESSION: 'CANCEL_CONTRACT_CESSION',
  RESEND_SIGN_REQUEST_FOR_CONTRACT_CESSION:
    'RESEND_SIGN_REQUEST_FOR_CONTRACT_CESSION',
  SEND_CONTRACT_CESSION_TO_SIGN: 'SEND_CONTRACT_CESSION_TO_SIGN',
  SEND_TO_DRAFT: 'SEND_TO_DRAFT',
  GENERATE_TERMINATION_LETTER: 'GENERATE_TERMINATION_LETTER',
  TERMINATE_CONTRACT: 'TERMINATE_CONTRACT',
  ASSIGN_FIDEICOMISO: 'ASSIGN_FIDEICOMISO'
};

export const ContractEvents = {
  CANCELLATION: 'CANCELLATION',
  CONTRACT_ASSIGNMENT: 'CONTRACT_ASSIGNMENT',
  TERMINATION: 'TERMINATION',
  CAPITAL_CONTRIBUTION: 'CAPITAL_CONTRIBUTION',
  SIGNATURE: 'SIGNATURE',
  CREATION: 'CREATION',
  EARLY_TERMINATION: 'EARLY_TERMINATION',
  FIELD_UPDATE: 'FIELD_UPDATE'
};

export const getContractEventsLabels = t => [
  {
    key: ContractEvents.CANCELLATION,
    name: t('CONTRACT_EVENTS_TYPES.CANCELLATION')
  },
  {
    key: ContractEvents.CONTRACT_ASSIGNMENT,
    name: t('CONTRACT_EVENTS_TYPES.CONTRACT_ASSIGNMENT')
  },
  {
    key: ContractEvents.TERMINATION,
    name: t('CONTRACT_EVENTS_TYPES.TERMINATION')
  },
  {
    key: ContractEvents.CAPITAL_CONTRIBUTION,
    name: t('CONTRACT_EVENTS_TYPES.CAPITAL_CONTRIBUTION')
  },
  {
    key: ContractEvents.SIGNATURE,
    name: t('CONTRACT_EVENTS_TYPES.SIGNATURE')
  },
  { key: ContractEvents.CREATION, name: t('CONTRACT_EVENTS_TYPES.CREATION') },
  {
    key: ContractEvents.EARLY_TERMINATION,
    name: t('CONTRACT_EVENTS_TYPES.CONTRACT_EARLY_TERMINATION')
  },
  {
    key: ContractEvents.FIELD_UPDATE,
    name: t('CONTRACT_EVENTS_TYPES.FIELD_UPDATE')
  }
];

// Label + value formatter for every field PATCH /contracts/:id/financial-terms
// can change, used by FieldUpdateDetailDialog's diff list. Labels reuse the
// same keys GeneralContractDetail.js already shows for these fields (rate,
// term, equipmentCost, initialPayment, residualPayment, cat) so a value reads
// the same way here as on the contract's own Detalles tab; formatters mirror
// that same screen's formatRate/formatPositiveAmount conventions. Only the two
// insurance labels are new (no prior "included?"/"annual value" label
// existed). One object instead of separate label/date-fields/boolean-fields
// lists, so a field's label and its formatting rule can't drift apart.
export const getContractFinancialTermsFieldConfig = t => ({
  rate: {
    label: t('CONTRACT_VIEW.RATE'),
    format: value => `${formatRate(value)} %`
  },
  term: {
    label: t('CONTRACT_VIEW.TERM'),
    format: value => `${value}`
  },
  residualPayment: {
    label: t('CONTRACT_VIEW.RESIDUAL'),
    format: value => formatPositiveAmount(value)
  },
  initialPayment: {
    label: t('CONTRACT_VIEW.INITIAL_PAYMENT'),
    format: value => formatPositiveAmount(value)
  },
  equipmentCost: {
    label: t('CONTRACT_VIEW.EQUIPMENT_COST'),
    format: value => formatPositiveAmount(value)
  },
  gracePeriod: {
    label: t('COMMON.GRACE_PERIOD'),
    format: value => translateGracePeriodValueToDaysForFirstRent(value, t)
  },
  cat: {
    label: t('CONTRACT_VIEW.CAT'),
    format: value => `${formatRate(value)} %`,
    // Mirrors GeneralContractDetail.js's own `contract.cat ? ... : NO_DATA`
    // check for this same field — 0 reads as "not set", not "0.0 %".
    isEmpty: value => !value
  },
  comment: {
    label: t('COMMON.COMMENT'),
    format: value => value
  },
  isInsuranceIncluded: {
    label: t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.INSURANCE_INCLUDED'),
    format: value => (value ? t('COMMON.YES') : t('COMMON.NO'))
  },
  insuranceAnnualValue: {
    label: t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.INSURANCE_ANNUAL_VALUE'),
    format: value => formatPositiveAmount(value)
  },
  startDate: {
    label: t('COMMON.START_DATE'),
    format: value => selectFormatDate(value)
  },
  firstRentDate: {
    label: t('CONTRACT_VIEW.FIRST_RENT_DATE'),
    format: value => selectFormatDate(value)
  }
});

// Fields actually read by FiscalBalanceCalculator / AccountingBalanceCalculator
// / ContractPaymentScheduleService (verified against those three files, not
// guessed) — used to flag a FIELD_UPDATE change as affecting the amortization
// schedule. `gracePeriod` is deliberately NOT here even though it looks like
// it should be: the calculators derive grace length from startDate/
// firstRentDate directly, never from the stored gracePeriod enum (see
// accounting-balance-calculator.ts's getGraceMonthsBeforeFirstPayment). `cat`
// and `comment` are stored but never read by any of the three files either.
export const CONTRACT_FINANCIAL_TERMS_IMPACT_FIELDS = [
  'rate',
  'term',
  'residualPayment',
  'initialPayment',
  'equipmentCost',
  'isInsuranceIncluded',
  'insuranceAnnualValue',
  'startDate',
  'firstRentDate'
];

export const ContractsComments = {
  CEDED: 'CEDED',
  EARLY_TERMINATED: 'EARLY_TERMINATED'
};

export const ContractsEventsStatus = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED'
};

export const TerminationLetterOpions = {
  SEND_TO_CLIENT: 'SEND_TO_CLIENT'
};

export default ContractStatus;

export const ContractPaymentForm = {
  BANK_TRANSFER: 'BANK_TRANSFER',
  CREDIT_CARD: 'CREDIT_CARD',
  AUTOMATIC_DEBIT: 'AUTOMATIC_DEBIT'
};
